/**
 * Dashboard Page
 *
 * Overview page with:
 * - Stats Cards
 * - n8n Workflow List
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, Workflow } from '../utils/api';
import { logger } from '../utils/logger';
import { showToast } from '../utils/toast';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorDisplay } from '../components/ErrorDisplay';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { GitBranch, Activity, Zap, Clock, Coins, TrendingUp, PauseCircle, PlayCircle, Loader2 } from 'lucide-react';

interface DashboardStats {
  totalWorkflows: number;
  activeWorkflows: number;
  totalExecutions: number;
}

interface AnalyticsStats {
  totalTimeSavedHours: number;
  totalCostSavedEuros: number;
  averageTimeSavedPerWorkflowHours: number;
}

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [analyticsStats, setAnalyticsStats] = useState<AnalyticsStats | null>(null);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [executionCounts, setExecutionCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Toggle active state
  const [toggleWorkflow, setToggleWorkflow] = useState<Workflow | null>(null);
  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch workflows, executions, and analytics in parallel
        const [workflowsData, executionsData, analyticsData] = await Promise.all([
          apiClient.getWorkflows(),
          apiClient.getExecutions(),
          apiClient.getAnalytics('all').catch((err) => {
            logger.warn('Failed to fetch analytics, continuing without:', err);
            return null;
          }),
        ]);

        const activeWorkflows = workflowsData.filter((w) => w.active).length;
        const totalExecutions = executionsData.length;

        // Calculate execution counts per workflow
        const counts: Record<string, number> = {};
        executionsData.forEach((execution) => {
          const workflowId = execution.workflowId;
          if (workflowId !== undefined && workflowId !== null) {
            const workflowIdKey = String(workflowId);
            counts[workflowIdKey] = (counts[workflowIdKey] || 0) + 1;
          }
        });

        logger.info('Dashboard data fetched', {
          totalWorkflows: workflowsData.length,
          activeWorkflows,
          totalExecutions,
          hasAnalytics: !!analyticsData,
          paginationNote: executionsData.length >= 12000 ? 'May be partially loaded (>12k)' : 'All loaded',
        });

        const dashboardStats: DashboardStats = {
          totalWorkflows: workflowsData.length,
          activeWorkflows,
          totalExecutions,
        };

        setStats(dashboardStats);
        setWorkflows(workflowsData);
        setExecutionCounts(counts);

        // Set analytics stats if available
        if (analyticsData) {
          // Net cost savings = human cost savings minus workflow execution costs
          const totalWorkflowCosts = analyticsData.workflows?.reduce(
            (sum, w) => sum + (w.cost_per_execution_euros || 0) * w.execution_count, 0
          ) || 0;
          setAnalyticsStats({
            totalTimeSavedHours: analyticsData.total_time_saved_hours,
            totalCostSavedEuros: analyticsData.total_cost_saved_euros - totalWorkflowCosts,
            averageTimeSavedPerWorkflowHours: analyticsData.average_time_saved_per_workflow_hours,
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
        logger.error('Dashboard data fetch error:', err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();

      // Auto-refresh dashboard every 30 seconds
      const intervalId = setInterval(() => {
        logger.debug('Auto-refreshing dashboard data');
        fetchDashboardData();
      }, 30000); // 30 seconds

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [user]);

  // Refetch function for reuse
  const refetchData = async () => {
    try {
      const [workflowsData, executionsData, analyticsData] = await Promise.all([
        apiClient.getWorkflows(),
        apiClient.getExecutions(),
        apiClient.getAnalytics('all').catch(() => null),
      ]);

      const activeWorkflows = workflowsData.filter((w) => w.active).length;

      const counts: Record<string, number> = {};
      executionsData.forEach((execution) => {
        const workflowId = execution.workflowId;
        if (workflowId !== undefined && workflowId !== null) {
          counts[String(workflowId)] = (counts[String(workflowId)] || 0) + 1;
        }
      });

      setStats({
        totalWorkflows: workflowsData.length,
        activeWorkflows,
        totalExecutions: executionsData.length,
      });
      setWorkflows(workflowsData);
      setExecutionCounts(counts);

      if (analyticsData) {
        const totalWorkflowCosts = analyticsData.workflows?.reduce(
          (sum, w) => sum + (w.cost_per_execution_euros || 0) * w.execution_count, 0
        ) || 0;
        setAnalyticsStats({
          totalTimeSavedHours: analyticsData.total_time_saved_hours,
          totalCostSavedEuros: analyticsData.total_cost_saved_euros - totalWorkflowCosts,
          averageTimeSavedPerWorkflowHours: analyticsData.average_time_saved_per_workflow_hours,
        });
      }
    } catch (err) {
      logger.error('Failed to refetch dashboard data:', err);
    }
  };

  // Toggle handlers
  const handleToggleClick = (workflow: Workflow) => {
    setToggleWorkflow(workflow);
    setIsToggleModalOpen(true);
  };

  const handleToggleConfirm = async () => {
    if (!toggleWorkflow) return;

    try {
      setIsToggling(true);
      const newActiveState = !toggleWorkflow.active;

      logger.info('Toggling workflow active status', {
        workflowId: toggleWorkflow.id,
        workflowName: toggleWorkflow.name,
        newActiveState,
      });

      await apiClient.setWorkflowActive(toggleWorkflow.id, newActiveState);

      showToast.success(
        newActiveState
          ? `Workflow "${toggleWorkflow.name}" wurde aktiviert`
          : `Workflow "${toggleWorkflow.name}" wurde deaktiviert`
      );

      // Update local state immediately
      setWorkflows((prev) =>
        prev.map((wf) =>
          wf.id === toggleWorkflow.id ? { ...wf, active: newActiveState } : wf
        )
      );

      // Update stats
      setStats((prev) => {
        if (!prev) return prev;
        const activeChange = newActiveState ? 1 : -1;
        return {
          ...prev,
          activeWorkflows: prev.activeWorkflows + activeChange,
        };
      });

      // Refetch after delay for consistency
      setTimeout(() => {
        refetchData();
      }, 2000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Aktualisieren des Workflow-Status';
      logger.error('Failed to toggle workflow active status', {
        workflowId: toggleWorkflow.id,
        error: message,
      });
      showToast.error(message);
    } finally {
      setIsToggling(false);
      setIsToggleModalOpen(false);
      setToggleWorkflow(null);
    }
  };

  const handleToggleCancel = () => {
    if (!isToggling) {
      setIsToggleModalOpen(false);
      setToggleWorkflow(null);
    }
  };

  // Generate confirmation modal content based on action
  const getToggleModalContent = () => {
    if (!toggleWorkflow) {
      return { title: '', message: '' };
    }

    if (toggleWorkflow.active) {
      return {
        title: 'Workflow deaktivieren',
        message: `Sind Sie sicher, dass Sie den Workflow "${toggleWorkflow.name}" deaktivieren möchten? Alle damit verbundenen automatisierten Prozesse werden gestoppt und sind nicht mehr funktionsfähig, bis der Workflow erneut aktiviert wird.`,
      };
    } else {
      return {
        title: 'Workflow aktivieren',
        message: `Sind Sie sicher, dass Sie den Workflow "${toggleWorkflow.name}" aktivieren möchten? Nach der Aktivierung werden automatisierte Prozesse gestartet, die möglicherweise API-Kosten verursachen und Ressourcen verbrauchen können.`,
      };
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  if (error) {
    return <ErrorDisplay message={error} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Welcome back, {user?.email}
          </p>
        </div>

        {/* Stats Cards - 6 Cards in 3x2 Grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
          {/* Row 1: Workflow Stats */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <GitBranch className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Total Workflows</p>
                  <p className="text-2xl font-semibold text-foreground">{stats?.totalWorkflows ?? 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Activity className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Active Workflows</p>
                  <p className="text-2xl font-semibold text-foreground">{stats?.activeWorkflows ?? 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Total Executions</p>
                  <p className="text-2xl font-semibold text-foreground">{stats?.totalExecutions ?? 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Row 2: Analytics Stats */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-secondary" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Gesparte Zeit</p>
                  <p className="text-2xl font-semibold text-foreground">{analyticsStats?.totalTimeSavedHours ?? 0}h</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                    <Coins className="w-5 h-5 text-secondary" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Gesparte Kosten</p>
                  <p className="text-2xl font-semibold text-foreground">
                    €{analyticsStats?.totalCostSavedEuros?.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }) ?? '0,00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-secondary" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Durchschn. Zeitersparnis</p>
                  <p className="text-2xl font-semibold text-foreground">{analyticsStats?.averageTimeSavedPerWorkflowHours ?? 0}h/Workflow</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* n8n Workflow List */}
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg leading-6 font-medium text-foreground mb-4">
              Workflows
            </h3>
            {workflows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No workflows found</p>
            ) : (
              <div className="overflow-hidden">
                <ul className="divide-y divide-border">
                  {workflows.map((workflow) => (
                    <li key={workflow.id} className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1">
                          <div className="flex-shrink-0">
                            <Badge variant={workflow.active ? 'default' : 'secondary'}>
                              {workflow.active ? 'Aktiv' : 'Inaktiv'}
                            </Badge>
                          </div>
                          <div className="ml-4 flex-1">
                            <div className="text-sm font-medium text-foreground">
                              {workflow.name || `Workflow ${workflow.id}`}
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                              <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs" title="Workflow ID">
                                {workflow.id}
                              </span>
                              {' • '}
                              Updated: {new Date(workflow.updatedAt).toLocaleDateString()}
                              {' • '}
                              Executions: {executionCounts[String(workflow.id)] || 0}
                              {workflow.tags && workflow.tags.length > 0 && (
                                <>
                                  {' • '}
                                  Tags: {workflow.tags.map((t) => t.name).join(', ')}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Toggle Button */}
                        <div className="ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleClick(workflow)}
                            disabled={isToggling && toggleWorkflow?.id === workflow.id}
                            title={workflow.active ? 'Workflow deaktivieren' : 'Workflow aktivieren'}
                            className={workflow.active
                              ? 'border-secondary/30 text-secondary hover:bg-secondary/10'
                              : 'border-primary/30 text-primary hover:bg-primary/10'
                            }
                          >
                            {isToggling && toggleWorkflow?.id === workflow.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : workflow.active ? (
                              <>
                                <PauseCircle className="w-4 h-4 mr-1" />
                                Deaktivieren
                              </>
                            ) : (
                              <>
                                <PlayCircle className="w-4 h-4 mr-1" />
                                Aktivieren
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Toggle Confirmation Modal */}
        <ConfirmationModal
          isOpen={isToggleModalOpen}
          onClose={handleToggleCancel}
          onConfirm={handleToggleConfirm}
          title={getToggleModalContent().title}
          message={getToggleModalContent().message}
          confirmLabel="Fortfahren"
          cancelLabel="Abbrechen"
          variant={toggleWorkflow?.active ? 'warning' : 'info'}
          isLoading={isToggling}
        />
      </div>
    </div>
  );
};


